import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SwitchTransition } from 'react-transition-group';
import { useTranslation } from '@wix/yoshi-flow-editor';

import type { IGroupMember } from 'store/members/types';
import { selectAnswersStatus } from 'store/selectors';
import { useController } from 'common/context/controller';

import { Fade } from 'wui/Fade';
import { Stack } from 'wui/Stack';
import { InputDialog } from 'wui/InputDialog';
import { DialogTitle } from 'wui/DialogTitle';
import { DialogContent } from 'wui/DialogContent';

import {
  GroupQuestion,
  GroupQuestionSkeleton,
} from 'common/components/GroupMembership/dialogs/GroupQuestionsDialog/GroupQuestion';

interface IMemberAnswersDialogProps {
  isOpen: boolean;
  groupId: string;
  member: IGroupMember;

  onClose(): void;
}

export function MemberAnswersDialog(props: IMemberAnswersDialogProps) {
  const { groupId, member, isOpen, onClose } = props;

  const { t } = useTranslation();

  const { members$ } = useController();
  const status = useSelector(
    selectAnswersStatus(member.siteMemberId as string),
  );

  useEffect(() => {
    if (!member.answers?.length && isOpen) {
      members$.fetchAnswers(groupId, member.siteMemberId as string);
    }
  }, [member.siteMemberId, groupId, isOpen]);

  return (
    <InputDialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="ma-dialog-title"
    >
      <DialogTitle
        id="ma-dialog-title"
        title={t('groups-web.membership-questions.answers-modal.title', {
          userName: member.name,
        })}
      />
      <DialogContent>
        <SwitchTransition>
          <Fade key={String(status.loading)} unmountOnExit>
            <Stack gap="SP2" direction="vertical">
              {status.loading ? (
                <>
                  <GroupQuestionSkeleton />
                  <GroupQuestionSkeleton />
                  <GroupQuestionSkeleton />
                  <GroupQuestionSkeleton />
                </>
              ) : (
                member.answers?.map((answer, index) => (
                  <GroupQuestion
                    disabled
                    index={index + 1}
                    key={answer.id}
                    question={answer.question}
                    required={answer.required}
                    value={answer.answer}
                  />
                ))
              )}
            </Stack>
          </Fade>
        </SwitchTransition>
      </DialogContent>
    </InputDialog>
  );
}

MemberAnswersDialog.displayName = 'MemberAnswersDialog';
